/**
 * Budget Negotiator™ (Patent pending)
 * ====================================
 * Automates the negotiation between Finance's desire for predictability
 * and Engineering's desire for speed — at the code level, before the
 * resource is ever provisioned.
 *
 * Three-section layout:
 *   1. How it works — contract.json + 3-band decision tree + sequence diagram
 *   2. Live demo — finance envelopes ↔ engineering PR queue, what-if simulator
 *   3. Renegotiations in flight
 *
 * Feb 25 2026 — Added dedicated "How it works" section per HN-launch
 * polish plan: a sample machine-readable contract, an explicit decision
 * tree, and the PR→merge sequence.
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, ShieldCheck, GitPullRequest, FileSignature, Handshake, Loader2, AlertOctagon, CheckCircle2, AlertTriangle, ArrowDown, ArrowRight } from 'lucide-react';
// v34.55 — promoted from BackToHomePill → PublicNavbar so visitors get the same top nav as /pilot, /wall-of-tenants, etc.
import PublicNavbar from '../components/PublicNavbar';
import { ENGINE_COUNT } from '../constants/product';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const DECISION_STYLE = {
  GREEN_AUTO_APPROVE:   { border: 'border-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-300', Icon: CheckCircle2 },
  YELLOW_REVIEW_NEEDED: { border: 'border-amber-500/40',   bg: 'bg-amber-500/10',   text: 'text-amber-300',   Icon: AlertTriangle },
  RED_RENEGOTIATE:      { border: 'border-rose-500/50',    bg: 'bg-rose-500/10',    text: 'text-rose-300',    Icon: AlertOctagon },
};

const utilizationColor = (pct) => {
  if (pct >= 100) return 'bg-rose-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
};

// Sample contract — sized to be readable in <30 lines
const SAMPLE_CONTRACT = `{
  "envelope_id": "env_payments_q1_2026",
  "cost_center": "payments-api",
  "owner": "cfo@example.com",
  "quarter": "2026-Q1",
  "monthly_envelope_usd": 18000,
  "thresholds": {
    "green_auto_approve_pct": 75,
    "yellow_review_pct":      90,
    "red_renegotiate_pct":   100
  },
  "signed_by": {
    "finance":     "cfo@example.com",
    "engineering": "vp-eng@example.com",
    "signed_at":   "2026-01-15T14:30:00Z",
    "signature_sha": "0x4a8c1e0a2d3f..."
  },
  "auto_actions": {
    "on_green":  "merge_pr",
    "on_yellow": "request_review_from_team_lead",
    "on_red":    "open_renegotiation_flow"
  }
}`;

// Static class maps — Tailwind JIT requires complete class names at build time
const BAND_STYLES = {
  emerald: { card: 'bg-emerald-500/5 border-emerald-500/30', heading: 'text-emerald-300', label: 'text-emerald-400' },
  amber:   { card: 'bg-amber-500/5 border-amber-500/30',     heading: 'text-amber-300',   label: 'text-amber-400' },
  rose:    { card: 'bg-rose-500/5 border-rose-500/30',       heading: 'text-rose-300',    label: 'text-rose-400' },
};

const STEP_STYLES = {
  emerald: { badge: 'bg-emerald-500/10 border-emerald-500/30', num: 'text-emerald-300', actor: 'text-emerald-400' },
  amber:   { badge: 'bg-amber-500/10 border-amber-500/30',     num: 'text-amber-300',   actor: 'text-amber-400' },
  rose:    { badge: 'bg-rose-500/10 border-rose-500/30',       num: 'text-rose-300',    actor: 'text-rose-400' },
};

const DECISION_BANDS = [
  {
    band: 'Green',
    range: '< 75% of envelope',
    color: 'emerald',
    Icon: CheckCircle2,
    title: 'Budget OK · cleared to merge',
    detail: 'PR auto-merges. Predicted cost stays well within envelope. No human approval needed.',
  },
  {
    band: 'Yellow',
    range: '75–90% of envelope',
    color: 'amber',
    Icon: AlertTriangle,
    title: 'Lead approval needed',
    detail: 'Routes to team-lead. They must explicitly approve before merge. CFO is notified async.',
  },
  {
    band: 'Red',
    range: '> 90% of envelope',
    color: 'rose',
    Icon: AlertOctagon,
    title: 'Re-envelope · structured renegotiation',
    detail: 'Merge blocked. Engineer files structured ask (revenue, strategic impact). Finance approves with digital signature → envelope is updated → PR unblocked.',
  },
];

const SEQUENCE_STEPS = [
  { who: 'Engineer',  what: 'Opens PR with infra change (Terraform / K8s manifest / IaC).', accent: 'emerald' },
  { who: 'CARTIE',    what: 'Predicts monthly $ delta from the PR diff. Looks up the cost-center envelope.', accent: 'emerald' },
  { who: 'Negotiator',what: 'Computes projected_utilization = (current + delta) / envelope. Classifies Green / Yellow / Red.', accent: 'amber' },
  { who: 'Decision',  what: 'On Green → posts ✓ check on PR + auto-merges. On Yellow → blocks merge until team-lead approves. On Red → opens renegotiation issue with revenue + strategic ask.', accent: 'amber' },
  { who: 'Finance',   what: 'If Red: reviews the structured ask. Approves with digital signature → envelope is bumped → PR unblocked + contract.json updated.', accent: 'rose' },
  { who: 'Audit',     what: 'Every contract signature, decision, and override is logged immutably for SOC 2 audit.', accent: 'emerald' },
];

const BudgetNegotiator = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatIf, setWhatIf] = useState({ cost_center: 'payments-api', predicted_monthly_delta_usd: 240 });
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    document.title = 'Budget Negotiator™ · Finance ↔ Engineering at code-time | CARTIEAI';
    axios.get(`${API_URL}/budget-negotiator/demo`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const runCheck = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/budget-negotiator/check`, whatIf);
      setWhatIfResult(r.data);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="budget-negotiator-page">
      <Helmet><title>Budget Negotiator™ · Finance ↔ Engineering at code-time | CARTIEAI</title></Helmet>
      <PublicNavbar />

      {/* Atmospheric orbs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[380px] h-[380px] rounded-full bg-teal-500/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-5 bg-emerald-500/10">
            <Sparkles className="w-3 h-3" /> Patent pending · auto-negotiation engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white" data-testid="bn-h1">
            Budget Negotiator<span className="text-emerald-400">™</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
            Most FinOps tools just <em>report</em> cost. This one <strong className="text-white">automates the negotiation</strong> between Finance's desire for predictability and Engineering's desire for speed — at the <strong className="text-emerald-300">code level, before the resource is ever provisioned.</strong>
          </p>
          <p className="mt-3 text-sm text-slate-400">
            Finance pre-signs quarterly budget envelopes per cost-center. Engineer opens a PR. Predicted cost is checked against the envelope automatically: <span className="text-emerald-300 font-semibold">green</span> auto-merges, <span className="text-amber-300 font-semibold">yellow</span> routes to team-lead, <span className="text-rose-300 font-semibold">red</span> opens a structured renegotiation flow.
          </p>
        </div>

        {/* ============================================================== */}
        {/* HOW IT WORKS — SECTION ADDED FOR HN LAUNCH                     */}
        {/* ============================================================== */}
        <div className="mt-16" data-testid="bn-how-it-works">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">How it works</h2>
          <p className="mt-2 text-sm text-slate-400 max-w-3xl">
            The full Finance ↔ Engineering contract is machine-readable and lives in your repo.
            Every PR is automatically classified into one of three decision bands before merge.
          </p>

          {/* 1. Contract.json + 3-band */}
          <div className="mt-8 grid lg:grid-cols-5 gap-6">
            {/* Contract */}
            <div className="lg:col-span-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
                <FileSignature className="w-3 h-3 inline mr-1" /> 1 · contract.json (signed by CFO + VP-Eng)
              </div>
              <pre className="rounded-xl border border-emerald-500/20 bg-slate-950/80 p-4 text-[12px] leading-relaxed font-mono text-emerald-200 overflow-x-auto whitespace-pre shadow-lg shadow-slate-900/40" data-testid="bn-contract-json">
{SAMPLE_CONTRACT}
              </pre>
              <p className="mt-2 text-[11px] text-slate-500">
                Stored in your repo at <code className="text-slate-300">.cartie/envelopes/*.json</code>. Version-controlled, signature-verified, audit-logged.
              </p>
            </div>

            {/* 3-band decision tree */}
            <div className="lg:col-span-3">
              <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
                <Handshake className="w-3 h-3 inline mr-1" /> 2 · 3-band decision tree
              </div>
              <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 shadow-lg shadow-slate-900/40" data-testid="bn-decision-tree">
                <div className="text-xs text-slate-400 mb-3">
                  CARTIE computes <code className="text-emerald-300">projected_utilization = (current_spend + predicted_delta) / monthly_envelope</code>
                </div>
                <div className="grid sm:grid-cols-3 gap-3" data-testid="bn-bands">
                  {DECISION_BANDS.map((b) => {
                    const Ic = b.Icon;
                    const cls = BAND_STYLES[b.color];
                    return (
                      <div
                        key={b.band}
                        className={`rounded-lg border p-4 ${cls.card}`}
                        data-testid={`bn-band-${b.band.toLowerCase()}`}
                      >
                        <div className={`flex items-center gap-2 font-bold text-sm ${cls.heading}`}>
                          <Ic className="w-4 h-4" /> {b.band}
                        </div>
                        <div className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${cls.label}`}>{b.range}</div>
                        <div className="text-xs text-white font-semibold mt-2">{b.title}</div>
                        <div className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{b.detail}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
                  Thresholds are configurable per envelope (defaults: 75% green / 90% yellow / 100% red).
                  All three decisions write to the audit log with the contract version, commit SHA, and signature hash.
                </div>
              </div>
            </div>
          </div>

          {/* 3. Sequence diagram */}
          <div className="mt-10">
            <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
              <GitPullRequest className="w-3 h-3 inline mr-1" /> 3 · sequence — engineer opens PR → merge or renegotiation
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 shadow-lg shadow-slate-900/40" data-testid="bn-sequence">
              <div className="space-y-2">
                {SEQUENCE_STEPS.map((s, i) => {
                  const cls = STEP_STYLES[s.accent];
                  return (
                    <div key={i} className="flex items-start gap-3" data-testid={`bn-seq-${i}`}>
                      <div className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg border ${cls.badge}`}>
                        <span className={`text-[11px] font-mono font-bold ${cls.num}`}>{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0 flex items-start gap-3">
                        <span className={`shrink-0 mt-1 text-[10px] font-mono font-bold uppercase tracking-widest w-20 ${cls.actor}`}>
                          {s.who}
                        </span>
                        <span className="text-sm text-slate-200 leading-relaxed">{s.what}</span>
                      </div>
                      {i < SEQUENCE_STEPS.length - 1 && (
                        <ArrowDown className="hidden sm:block w-4 h-4 text-slate-600 mt-2" />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-white/5 text-[11px] text-slate-500 leading-relaxed">
                <strong className="text-slate-300">SLA:</strong> Green decisions in &lt;200ms · Yellow notifications within 30s · Red renegotiations carry a 48h Finance SLA.
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* LIVE DEMO                                                       */}
        {/* ============================================================== */}
        <div className="mt-20" data-testid="bn-live-demo">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Live demo</h2>
          <p className="mt-2 text-sm text-slate-400 max-w-3xl">
            Real envelopes signed by a sample Finance team on the left. Open PRs from a sample Engineering team on the right. Every PR has already been auto-classified.
          </p>

          {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-16 text-slate-500" />}

          {data && !loading && (
            <>
              {/* TWO-COLUMN LAYOUT */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                {/* FINANCE SIDE */}
                <Card className="bg-slate-900/60 border-emerald-500/30">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-white">
                      <FileSignature className="w-5 h-5 text-emerald-400" /> Finance · Budget envelopes
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Quarterly envelopes pre-signed by the CFO. The contract Finance and Engineering both honor.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3" data-testid="bn-envelopes">
                    {data.envelopes.map((e) => (
                      <div key={e.cost_center} className="p-3 rounded-md border border-emerald-500/20 bg-slate-950/60">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-white">{e.cost_center}</div>
                            <div className="text-[10px] text-slate-500">owner: {e.owner}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-mono text-slate-200">{fmt(e.current_spend_mtd_usd)} / {fmt(e.monthly_envelope_usd)}</div>
                            <div className={`text-xs font-semibold ${e.envelope_utilization_pct >= 95 ? 'text-rose-300' : e.envelope_utilization_pct >= 80 ? 'text-amber-300' : 'text-emerald-300'}`}>
                              {e.envelope_utilization_pct}% used
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 w-full h-1.5 rounded bg-slate-800 overflow-hidden">
                          <div className={`h-full ${utilizationColor(e.envelope_utilization_pct)}`} style={{ width: `${Math.min(100, e.envelope_utilization_pct)}%` }} />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* ENGINEERING SIDE */}
                <Card className="bg-slate-900/60 border-emerald-500/30">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-white">
                      <GitPullRequest className="w-5 h-5 text-emerald-400" /> Engineering · PR queue
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Each open PR is auto-checked against its cost-center's envelope. Three outcomes.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3" data-testid="bn-pr-queue">
                    {data.pr_queue.map((pr) => {
                      const style = DECISION_STYLE[pr.decision];
                      const { Icon } = style;
                      return (
                        <div key={pr.pr_number} className={`p-3 rounded-md border ${style.border} ${style.bg}`} data-testid={`bn-pr-${pr.pr_number}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-mono text-slate-500">PR #{pr.pr_number} · {pr.author}</div>
                              <div className="text-sm font-semibold truncate text-white">{pr.pr_title}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-sm font-bold font-mono text-white">{fmt(pr.predicted_monthly_delta_usd)}/mo</div>
                              <div className="text-[10px] text-slate-500">→ {pr.projected_envelope_utilization_pct}%</div>
                            </div>
                          </div>
                          <div className={`mt-2 flex items-center gap-2 ${style.text} text-xs font-semibold`}>
                            <Icon className="w-4 h-4" /> {pr.decision_label}
                          </div>
                          <div className="text-xs text-slate-400 mt-1.5">{pr.reasoning}</div>
                          {pr.contract_snapshot && (
                            <div className="mt-2 text-[10px] font-mono text-emerald-300">
                              ✓ Contract: commit {pr.contract_snapshot.commit_sha} · envelope {pr.contract_snapshot.envelope_version} · reconcile-by {pr.contract_snapshot.reconcile_by}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>

              {/* WHAT-IF SIMULATOR */}
              <Card className="mt-8 bg-slate-900/60 border-emerald-500/40" data-testid="bn-whatif">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-white">
                    <Handshake className="w-5 h-5 text-emerald-400" /> What-if simulator
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Try the auto-negotiator on any predicted PR delta — see the merge decision instantly.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-end gap-3" data-testid="bn-whatif-form">
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Cost-center</label>
                      <select
                        value={whatIf.cost_center}
                        onChange={(e) => setWhatIf({ ...whatIf, cost_center: e.target.value })}
                        className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-200"
                        data-testid="bn-whatif-cost-center"
                      >
                        {data.envelopes.map((e) => (
                          <option key={e.cost_center} value={e.cost_center}>{e.cost_center}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Predicted +$/month</label>
                      <input
                        type="number"
                        value={whatIf.predicted_monthly_delta_usd}
                        onChange={(e) => setWhatIf({ ...whatIf, predicted_monthly_delta_usd: Number(e.target.value) })}
                        className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm w-32 font-mono text-slate-200"
                        data-testid="bn-whatif-delta"
                      />
                    </div>
                    <Button onClick={runCheck} disabled={running} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold shadow-md shadow-emerald-500/20" data-testid="bn-whatif-run">
                      {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                      Check
                    </Button>
                  </div>

                  {whatIfResult && (
                    <div className={`mt-4 p-4 rounded-md border ${DECISION_STYLE[whatIfResult.decision]?.border} ${DECISION_STYLE[whatIfResult.decision]?.bg}`} data-testid="bn-whatif-result">
                      <div className={`flex items-center gap-2 ${DECISION_STYLE[whatIfResult.decision]?.text} font-semibold text-base`}>
                        {(() => {
                          const Ic = DECISION_STYLE[whatIfResult.decision]?.Icon;
                          return Ic ? <Ic className="w-5 h-5" /> : null;
                        })()}
                        {whatIfResult.decision_label}
                      </div>
                      <div className="text-sm text-slate-300 mt-1">{whatIfResult.reasoning}</div>
                      {whatIfResult.requires_signoff_from && (
                        <div className="text-xs text-slate-500 mt-2">→ Requires sign-off from: <span className="font-mono text-slate-300">{whatIfResult.requires_signoff_from}</span></div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* RENEGOTIATIONS IN FLIGHT */}
              {data.renegotiations.length > 0 && (
                <Card className="mt-8 bg-slate-900/60 border-rose-500/30">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-white">
                      <AlertOctagon className="w-5 h-5 text-rose-400" /> Renegotiations in flight · awaiting Finance
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Red-band PRs file structured asks with revenue + strategic-impact justification. Finance approves with a digital signature → envelope is updated → engineer is unblocked.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3" data-testid="bn-renegs">
                    {data.renegotiations.map((r) => (
                      <div key={r.id} className="p-3 rounded-md border border-rose-500/30 bg-rose-500/5">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="text-xs font-mono text-slate-500">{r.id} · PR #{r.pr_number} · {r.filed_by}</div>
                          <Badge variant="outline" className="border-rose-500/40 text-rose-300 text-[10px]">SLA {r.sla_hours_remaining}h left</Badge>
                        </div>
                        <div className="text-sm font-semibold text-white">Ask: +{fmt(r.ask_monthly_increase_usd)}/mo</div>
                        <div className="text-xs text-slate-300 mt-1.5"><strong className="text-slate-200">Why:</strong> {r.ask_summary}</div>
                        <div className="text-xs text-slate-300 mt-1"><strong className="text-slate-200">Revenue impact:</strong> {r.revenue_impact_summary}</div>
                        <div className="text-xs text-slate-300 mt-1"><strong className="text-slate-200">Strategic:</strong> {r.strategic_impact_summary}</div>
                        <div className="text-[10px] text-slate-500 mt-2">→ Approver: <span className="font-mono text-slate-300">{r.approver}</span></div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        <div className="mt-20 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 sm:p-10 text-center shadow-lg shadow-emerald-500/10" data-testid="bn-cta">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-emerald-500 shadow-md shadow-emerald-500/30 mx-auto mb-4">
            <Handshake className="w-7 h-7 text-slate-950" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Sign your first envelope in 5 minutes</h3>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto font-medium">
            14-day pilot · no card · we ship a sample <code className="text-emerald-300">contract.json</code> for your top 3 cost-centers and wire the auto-negotiator into your GitHub Actions.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/signup" className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/20 hover:-translate-y-0.5 transition-all" data-testid="bn-signup-btn">
              Start free pilot <ArrowRight className="w-4 h-4 inline ml-1" />
            </a>
            <a href="/patent-portfolio" className="px-6 py-2.5 rounded-lg border border-white/15 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-300 text-slate-300 text-sm font-semibold transition-all">
              ← Back to all {ENGINE_COUNT} engines
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetNegotiator;
